/* Groups feed items by topic section based on user's sectionGroupings setting.
   Items are ordered section-by-section; unmatched items go into an "Other" group at the end.
   Returns the sorted items and a function to look up the section name for any item. */

import { useMemo, useState, useCallback } from "react";
import type { FeedItemData } from "@/components/feed/FeedItemRow";
import { useSectionGroupings } from "@/hooks/useSectionGroupings";
import type { SectionGrouping } from "@/lib/defaultSectionGroupings";

const OTHER_SECTION_NAME = "Other";

export interface SectionInfo {
  name: string;
  count: number;
}

function findSectionForItem(item: FeedItemData, groupings: SectionGrouping[]): string {
  const itemTags = item.sourceTags ?? [];
  for (const section of groupings) {
    if (section.tags.some((tag) => itemTags.includes(tag))) {
      return section.name;
    }
  }
  return OTHER_SECTION_NAME;
}

interface GroupedFeedItemsOptions {
  fullTagCounts?: Record<string, number>;
  onFilterChange?: (tags: string[] | undefined) => void;
}

export function useGroupedFeedItems(items: FeedItemData[], options?: GroupedFeedItemsOptions) {
  const { sectionsEnabled, sectionGroupings } = useSectionGroupings();
  const [startSection, setStartSection] = useState<string | null>(null);

  const result = useMemo(() => {
    if (!sectionsEnabled || sectionGroupings.length === 0) {
      return {
        groupedItems: items,
        getSectionName: () => undefined as string | undefined,
        getSectionCount: () => 0,
        sections: [] as SectionInfo[],
      };
    }

    const sectionOrder = [...sectionGroupings.map((s) => s.name), OTHER_SECTION_NAME];
    const buckets = new Map<string, FeedItemData[]>();
    for (const name of sectionOrder) {
      buckets.set(name, []);
    }

    for (const item of items) {
      const sectionName = findSectionForItem(item, sectionGroupings);
      buckets.get(sectionName)!.push(item);
    }

    // Build sections list with full counts when available.
    // fullTagCounts may contain per-section deduplicated counts (keyed by section name)
    // or per-tag counts (keyed by tag). We check for section-name keys first.
    const fullTagCounts = options?.fullTagCounts;
    const sections: SectionInfo[] = [];
    for (const name of sectionOrder) {
      let count: number;
      if (fullTagCounts && fullTagCounts[name] !== undefined) {
        // Direct section-name key — deduplicated count from backend
        count = fullTagCounts[name];
      } else if (fullTagCounts && name !== OTHER_SECTION_NAME) {
        // Fallback: sum per-tag counts (may over-count due to tag overlap)
        const section = sectionGroupings.find((s) => s.name === name);
        count = section
          ? section.tags.reduce((sum, tag) => sum + (fullTagCounts[tag] ?? 0), 0)
          : 0;
      } else {
        // For "Other" or when no fullTagCounts, use loaded items count
        count = buckets.get(name)!.length;
      }
      if (count > 0) {
        sections.push({ name, count });
      }
    }

    // Reorder sections if a start section is selected
    let orderedSections = sectionOrder;
    if (startSection) {
      const idx = sectionOrder.indexOf(startSection);
      if (idx > 0) {
        orderedSections = [...sectionOrder.slice(idx), ...sectionOrder.slice(0, idx)];
      }
    }

    const groupedItems: FeedItemData[] = [];
    const itemSectionMap = new Map<string, string>();

    for (const name of orderedSections) {
      const sectionItems = buckets.get(name)!;
      for (const item of sectionItems) {
        groupedItems.push(item);
        itemSectionMap.set(item._id, name);
      }
    }

    function getSectionName(itemId: string): string | undefined {
      return itemSectionMap.get(itemId);
    }

    function getSectionCount(sectionName: string): number {
      return buckets.get(sectionName)?.length ?? 0;
    }

    return { groupedItems, getSectionName, getSectionCount, sections };
  }, [items, sectionsEnabled, sectionGroupings, startSection, options?.fullTagCounts]);

  const onFilterChange = options?.onFilterChange;
  const jumpToSection = useCallback((sectionName: string) => {
    setStartSection(sectionName);

    if (onFilterChange) {
      if (sectionName === OTHER_SECTION_NAME) {
        onFilterChange(undefined);
        return;
      }
      const section = sectionGroupings.find((s) => s.name === sectionName);
      onFilterChange(section?.tags);
    }
  }, [sectionGroupings, onFilterChange]);

  // Auto-track current section during swiping (no filter reload).
  // Keeps section ordering stable when loadMore brings in items from earlier sections.
  const trackSection = useCallback((sectionName: string) => {
    setStartSection(sectionName);
  }, []);

  const clearSectionTarget = useCallback(() => {
    setStartSection(null);
  }, []);

  return {
    ...result,
    jumpToSection,
    trackSection,
    clearSectionTarget,
    targetSection: startSection,
  };
}
